import { useMemo, useState, type FormEvent, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, Loader2, Search } from 'lucide-react';
import { ProfessionalCard } from '@/components';
import {
	useGetAllProfessionalsPublic,
	useGetServices,
	useGetSessions,
} from '@/services';
import { encodeUrl, formatProfessionalShortcutName } from '@/utils';
import { sortByMatchRank, useMatchedProfessionalRanks } from '@/hooks';
import { cn } from '@/lib/utils';
import {
	userHomeActionsWrapClassName,
	userHomePillButtonClassName,
	userHomePillInputClassName,
	userHomeSectionLabelClassName,
} from './userHomeCardStyles';

type UserHomeServiceSearchProps = {
	className?: string;
	showFeaturedPsychologists?: boolean;
};

const FEATURED_PSYCHOLOGISTS_COUNT = 3;

const normalizeServiceText = (value: string) =>
	value
		.toLocaleLowerCase('pt-BR')
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '');

const findPsychologistService = (services: Services.Service[]) =>
	services.find((service) => {
		const haystack = normalizeServiceText(
			`${service.service_name} ${service.professional_label ?? ''}`,
		);
		return haystack.includes('psicolog');
	});

const UserHomeServiceSearch = ({
	className,
	showFeaturedPsychologists = false,
}: UserHomeServiceSearchProps) => {
	const navigate = useNavigate();
	const [searchTerm, setSearchTerm] = useState('');
	const { data: services, isLoading } = useGetServices({
		filters: { active: true, type: 'PROFESSION' },
	});

	const psychologistService = useMemo(
		() => findPsychologistService(services ?? []),
		[services],
	);

	const { data: lastBookedSessionData } = useGetSessions(
		{
			isMe: true,
			limit: 1,
			page: 1,
			sort: 'created_at',
			sort_direction: 'desc',
		},
		{ refetchOnMount: 'always' },
	);

	const lastBookedProfessional = useMemo(() => {
		const session = lastBookedSessionData?.data?.[0];
		const professional = session?.professional;
		const professionalId =
			professional?.id ?? session?.professional_id ?? null;

		if (!session || !professionalId) return null;

		return {
			id: professionalId,
			name: professional
				? formatProfessionalShortcutName(professional)
				: 'Último profissional',
			serviceId: session.service_id,
		};
	}, [lastBookedSessionData?.data]);

	const otherServices = useMemo(() => {
		const list = services ?? [];
		if (!psychologistService) return list;
		return list.filter((service) => service.id !== psychologistService.id);
	}, [services, psychologistService]);

	const { data: featuredProfessionalsData, isFetching: isLoadingFeatured } =
		useGetAllProfessionalsPublic(
			{
				...(psychologistService?.id && {
					service_id: psychologistService.id,
				}),
			},
			{
				enabled: showFeaturedPsychologists && !isLoading,
			},
		);

	/** Match da conta logada: os indicados ocupam os destaques da home. */
	const matchRanks = useMatchedProfessionalRanks();

	const featuredPsychologists = useMemo(() => {
		const list =
			featuredProfessionalsData?.professionals ??
			featuredProfessionalsData?.data ??
			[];

		const psychologists = psychologistService
			? list
			: list.filter((professional) => {
					const serviceName =
						professional.professional_services?.[0]?.service
							?.service_name ?? '';
					return normalizeServiceText(serviceName).includes(
						'psicolog',
					);
				});

		// Quem respondeu ao match vê o próprio match nos destaques; os demais,
		// a ordem do admin que veio da API.
		return sortByMatchRank(psychologists, matchRanks).slice(
			0,
			FEATURED_PSYCHOLOGISTS_COUNT,
		);
	}, [featuredProfessionalsData, psychologistService, matchRanks]);

	const buildNameSearchUrl = (name: string) => {
		const params = new URLSearchParams();
		params.set('search', name.trim());
		params.set('name_search', '1');
		return `/consulta?${params.toString()}`;
	};

	const buildConsultaUrl = () => {
		const params = new URLSearchParams();

		if (psychologistService) {
			params.set('service_id', psychologistService.id);
		} else {
			params.set('service', 'psychologist');
		}

		return `/consulta?${params.toString()}`;
	};

	const handleNameSearch = (event?: FormEvent) => {
		event?.preventDefault();
		const trimmed = searchTerm.trim();
		if (!trimmed) return;
		navigate(buildNameSearchUrl(trimmed));
	};

	const handleNameKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
		if (event.key === 'Enter') {
			event.preventDefault();
			handleNameSearch();
		}
	};

	const handleServiceClick = (serviceId: string) => {
		navigate(`/consulta?service_id=${encodeURIComponent(serviceId)}`);
	};

	const handleLastBookedProfessionalClick = () => {
		if (!lastBookedProfessional) return;

		const params = new URLSearchParams();

		if (lastBookedProfessional.serviceId) {
			params.set('service_id', lastBookedProfessional.serviceId);
		}

		params.set('d', encodeUrl({ s: 2, p: lastBookedProfessional.id }));
		navigate(`/consulta?${params.toString()}`);
	};

	const handleBrowsePsychologists = () => {
		navigate(buildConsultaUrl());
	};

	const handleFeaturedProfessionalClick = (
		professionalId: string,
		slot?: { start_time: string },
	) => {
		const params = new URLSearchParams();

		if (psychologistService) {
			params.set('service_id', psychologistService.id);
		} else {
			params.set('service', 'psychologist');
		}

		params.set(
			'd',
			encodeUrl(
				slot
					? { s: 2, p: professionalId, t: slot.start_time }
					: { s: 2, p: professionalId },
			),
		);
		navigate(`/consulta?${params.toString()}`);
	};

	const pillChevron = (
		<ChevronRight
			size={16}
			className="-mr-0.5 shrink-0 text-zinc-500 transition-transform group-hover:translate-x-0.5"
			aria-hidden
		/>
	);

	return (
		<section className={cn('w-full space-y-4 pb-8 md:pb-12', className)}>
			<div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
				<div className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-brand-orange/10 text-brand-orange">
					<Search size={24} />
				</div>
				<div className="space-y-1">
					<h2 className="text-2xl font-bold tracking-tight text-zinc-900 md:text-3xl">
						Buscar profissional
					</h2>
					<p className="text-sm text-zinc-500">
						Digite o nome ou escolha uma opção abaixo para agendar
					</p>
				</div>
			</div>

			<div className="w-full">
				{isLoading ? (
					<div className="flex justify-center py-6">
						<Loader2 className="size-6 animate-spin text-brand-orange" />
					</div>
				) : (
					<div className="space-y-4">
						<div className="space-y-2.5">
							<p className={userHomeSectionLabelClassName}>
								Buscar por nome
							</p>
							<form
								onSubmit={handleNameSearch}
								className="flex flex-row items-center gap-2"
							>
								<input
									id="home-professional-name-search"
									type="search"
									value={searchTerm}
									onChange={(event) =>
										setSearchTerm(event.target.value)
									}
									onKeyDown={handleNameKeyDown}
									placeholder="Ex.: Maria Silva"
									className={cn(
										userHomePillInputClassName,
										'min-w-0 flex-1',
									)}
								/>
								<button
									type="submit"
									disabled={!searchTerm.trim()}
									className={cn(
										userHomePillButtonClassName,
										'shrink-0',
									)}
								>
									<span>Buscar</span>
									{pillChevron}
								</button>
							</form>
						</div>

						<div className="space-y-2.5">
							<div className={userHomeActionsWrapClassName}>
								{lastBookedProfessional ? (
									<button
										type="button"
										onClick={
											handleLastBookedProfessionalClick
										}
										className={userHomePillButtonClassName}
									>
										<span>
											{lastBookedProfessional.name}
										</span>
										{pillChevron}
									</button>
								) : null}

								{psychologistService && (
									<button
										type="button"
										onClick={handleBrowsePsychologists}
										className={userHomePillButtonClassName}
									>
										<span>
											{showFeaturedPsychologists
												? 'Ver todos os psicólogos'
												: psychologistService.service_name}
										</span>
										{pillChevron}
									</button>
								)}

								{otherServices.map((service) => (
									<button
										key={service.id}
										type="button"
										onClick={() =>
											handleServiceClick(service.id)
										}
										className={userHomePillButtonClassName}
									>
										<span>{service.service_name}</span>
										{pillChevron}
									</button>
								))}

								<button
									type="button"
									onClick={() => navigate('/consulta')}
									className={userHomePillButtonClassName}
								>
									<span>Ver todos</span>
									{pillChevron}
								</button>
							</div>
						</div>

						{showFeaturedPsychologists ? (
							<div className="space-y-2.5">
								<p className={userHomeSectionLabelClassName}>
									Psicólogos disponíveis
								</p>
								{isLoadingFeatured ? (
									<div className="flex justify-center py-6">
										<Loader2 className="size-6 animate-spin text-brand-orange" />
									</div>
								) : featuredPsychologists.length > 0 ? (
									<div className="mb-4 flex w-full flex-col gap-4 md:mb-6">
										{featuredPsychologists.map(
											(professional) => {
												const serviceName =
													professional
														.professional_services?.[0]
														?.service?.service_name;

												return (
													<ProfessionalCard
														key={professional.id}
														professional_id={
															professional.id
														}
														className="border-stone-300"
														name_prefix={
															professional.name_prefix
														}
														first_name={
															professional.first_name
														}
														last_name={
															professional.last_name
														}
														profile_photo_url={
															professional.profile_photo_url ??
															undefined
														}
														video_url={
															professional.video_url
														}
														crm_or_crp={
															professional.crm_or_crp
														}
														brief_description={
															professional.brief_description
														}
														service_name={
															serviceName
														}
														highlight_tag={
															professional.highlight_tag
														}
														main_approach={
															professional.main_approach
														}
														secondary_approaches={
															professional.secondary_approaches
														}
														focus_topics={
															professional.focus_topics
														}
														matchRank={
															matchRanks[
																professional.id
															] ?? null
														}
														isSelected={false}
														selectedButtonText="Continuar"
														onClick={() =>
															handleFeaturedProfessionalClick(
																professional.id,
															)
														}
														onScheduleSlot={(
															slot,
														) =>
															handleFeaturedProfessionalClick(
																professional.id,
																slot,
															)
														}
													/>
												);
											},
										)}
									</div>
								) : null}
							</div>
						) : null}
					</div>
				)}
			</div>
		</section>
	);
};

export { UserHomeServiceSearch };
